import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { leaderBoardQuery, type LeaderBoardRow } from '../../api/easeedeskDashboards'
import { Content } from '../../ui/Shell'
import { rangeOf, type RangeName } from './range'
import { NotInstalled } from './WallHeader'

/**
 * "Leader Board" - `easeedesk-dashboards/Leader_board::index`, macheta
 * `views/version-3/dashboards/easeedesk/leader_board/index.php` si randurile
 * pe care le desena `LeaderBoard` din `public/js/dashboards/easeedesk.js`.
 *
 * Acelasi tabel verde, cu aceleasi treisprezece coloane si aceleasi masuri;
 * pozitia se numara tot din foaie (`counter-increment`), ca in macheta, si
 * mediile se rotunjesc si primesc "m" ca in JS. Sub el, "Total TP".
 *
 * CE S-A SCHIMBAT LA FATA. Filtrele stateau intr-un sertar din dreapta
 * (`.page-quick-sidebar`) cu doua casute de data si un buton Apply; aici sunt
 * scurtaturile de perioada, aceleasi cu ale celorlalte tablouri. Prima e
 * "Last 24 Hours", fereastra cu care se deschidea ecranul.
 */

const STYLE = `
.od-leader-board { counter-reset: Serial; }
.od-leader-board table { border-collapse: separate; width: 100%; }
.od-leader-board th {
  font-size: 20px; height: 125px; line-height: 60px; background-color: #0c834d; color: #F2F2F2;
  text-align: center; border-left: 5px solid white; border-bottom: 5px solid white;
}
.od-leader-board td {
  font-size: 20px; height: 70px; line-height: 60px; background-color: #0c834d; color: #F2F2F2;
  text-align: center; border-left: 5px solid white; border-bottom: 5px solid white;
}
.od-leader-board tbody tr td:first-child:before { counter-increment: Serial; content: counter(Serial); }
.od-leader-board .od-total { width: 25.6%; }
`

/** `Math.round(v) + "m"`, ca in JS-ul vechi. */
function minutes(value: number): string {
  return `${Math.round(value)}m`
}

const AVERAGES: (keyof LeaderBoardRow)[] = ['avg_progress', 'avg_connected', 'avg_tookover', 'avg_resolved']

export function LeaderBoardPage() {
  const [rangeName, setRangeName] = useState<RangeName>('Last 24 Hours')
  const q = useQuery(leaderBoardQuery(rangeOf(rangeName)))
  const page = q.data

  return (
    <Content>
      <style>{STYLE}</style>
      <div className="od-leader-board">
        <div className="row mb-5">
          <div className="col-md-9">
            <h3>{page?.title ?? 'Leader Board'}</h3>
          </div>
          <div className="col-md-3">
            <select
              aria-label="Date range"
              className="form-control"
              value={rangeName}
              onChange={(e) => setRangeName(e.target.value as RangeName)}
            >
              {(['Last 24 Hours', 'Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'This Month', 'Last Month'] as RangeName[]).map(
                (name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                )
              )}
            </select>
          </div>
        </div>
        <hr />

        {page && !page.ready ? <NotInstalled what="Leader Board" /> : null}

        <table id="myTable">
          <thead>
            <tr>
              {(page?.columns ?? []).map((column) => (
                <th key={column.key} style={{ width: `${column.width}%` }}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(page?.rows ?? []).map((row) => (
              <tr key={row.user_id}>
                {/* Pozitia o scrie foaia, ca in macheta: celula ramane goala. */}
                <td style={{ width: '5%' }}></td>
                <td style={{ width: '12%' }}>{row.name}</td>
                <td style={{ width: '7%' }}>{row.final_score}</td>
                {AVERAGES.map((key) => (
                  <td key={key} style={{ width: '7%' }}>
                    {minutes(Number(row[key]))}
                  </td>
                ))}
                <td style={{ width: '7%' }}>{Math.round(row.miss_tookover)}</td>
                <td style={{ width: '7%' }}>{Math.round(row.hit_tookover)}</td>
                <td style={{ width: '7%' }}>{Math.round(row.miss_connected)}</td>
                <td style={{ width: '7%' }}>{Math.round(row.hit_connected)}</td>
                <td style={{ width: '7%' }}>{Math.round(row.avg_feedback)}</td>
                <td style={{ width: '7%' }}>{Math.round(row.tickets)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {page?.total === null || page?.total === undefined ? null : (
          <table className="od-total" id="myTableTotal">
            <tbody>
              <tr>
                <td>Total TP</td>
                <td>{page.total}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </Content>
  )
}
